// src/pages/Appli/AppliAdmin/ListingClients/components/AssociationsTable.jsx

import React from "react";

import { FaStar } from "react-icons/fa";

import AssociationActions from "./AssociationActions";

import InlineStatutSelect from "../ui/InlineStatutSelect";

import {
  formatAssociationDate,
  getReminderState,
  splitDisciplines,
} from "../utils/association.utils";

const fallbackLogo = "/images/no-image.jpg";

function TableHeader({ children, className = "" }) {
  return (
    <th
      className={[
        "whitespace-nowrap px-4 py-3",
        "text-left text-xs font-semibold",
        "uppercase tracking-wide",
        "text-slate-500 dark:text-slate-400",
        className,
      ].join(" ")}
    >
      {children}
    </th>
  );
}

function TableCell({ children, className = "" }) {
  return (
    <td
      className={`px-4 py-4 align-middle ${className}`}
    >
      {children}
    </td>
  );
}

function ContactList({ prospects = [] }) {
  if (!prospects.length) {
    return (
      <span className="text-xs italic text-slate-400">
        Aucun contact
      </span>
    );
  }

  const visibleContacts = prospects.slice(0, 2);
  const remaining = prospects.length - 2;

  return (
    <div className="space-y-1">
      {visibleContacts.map((prospect) => (
        <div
          key={prospect.id}
          className="text-xs text-slate-700 dark:text-slate-300"
        >
          <span className="font-semibold">
            {prospect.nom}
          </span>{" "}
          {prospect.prenom}
        </div>
      ))}

      {remaining > 0 ? (
        <div className="text-[11px] font-medium text-violet-600 dark:text-violet-300">
          + {remaining} autre
          {remaining > 1 ? "s" : ""}
        </div>
      ) : null}
    </div>
  );
}

function FollowUpCell({ association }) {
  const reminder = getReminderState(
    association.date_relance_asso,
  );

  return (
    <div className="min-w-36 space-y-2">
      <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-400">
          Dernier contact
        </div>

        <div className="mt-0.5 text-xs font-medium text-slate-700 dark:text-slate-300">
          {formatAssociationDate(
            association.last_contact_asso,
          )}
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-400">
          Relance
        </div>

        {association.date_relance_asso ? (
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
              {formatAssociationDate(
                association.date_relance_asso,
              )}
            </span>

            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${reminder.tone}`}
            >
              {reminder.label}
            </span>
          </div>
        ) : (
          <div className="mt-0.5 text-xs italic text-slate-400">
            Non planifiée
          </div>
        )}
      </div>
    </div>
  );
}

export function AssociationsTable({
  data = [],
  deletingId = null,
  updatingStatusIds = [],
  onEdit,
  onDelete,
  onQuickView,
  onStatusChange,
}) {
  return (
    <div className="w-full overflow-x-auto">
      <table className="min-w-[1180px] w-full text-left text-sm">
        <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur dark:bg-slate-900/95">
          <tr className="border-b border-slate-200 dark:border-slate-800">
            <TableHeader>Association</TableHeader>
            <TableHeader>
              Produit et disciplines
            </TableHeader>
            <TableHeader>Statut</TableHeader>
            <TableHeader>Suivi</TableHeader>
            <TableHeader>Contacts</TableHeader>
            <TableHeader className="text-center">
              Actions
            </TableHeader>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {data.map((association) => {
            const disciplines = splitDisciplines(
              association.discipline_asso,
            );

            const statusUpdating =
              updatingStatusIds.includes(
                association.id,
              );

            return (
              <tr
                key={association.id}
                className="bg-white transition hover:bg-slate-50/80 dark:bg-slate-900 dark:hover:bg-slate-800/50"
              >
                <TableCell>
                  <div className="flex min-w-60 items-center gap-3">
                    <div className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white p-1 dark:border-slate-700">
                      <img
                        src={
                          association.logo_asso ||
                          fallbackLogo
                        }
                        alt=""
                        className="h-full w-full object-contain"
                        onError={(event) => {
                          event.currentTarget.onerror =
                            null;

                          event.currentTarget.src =
                            fallbackLogo;
                        }}
                      />

                      {association.is_favorite ? (
                        <span className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-[9px] text-white shadow">
                          <FaStar />
                        </span>
                      ) : null}
                    </div>

                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() =>
                          onQuickView?.(association)
                        }
                        className="max-w-56 truncate text-left font-semibold text-slate-900 transition hover:text-violet-600 dark:text-white dark:hover:text-violet-300"
                        title={association.nom_asso}
                      >
                        {association.nom_asso || "—"}
                      </button>

                      <div className="mt-0.5 text-[11px] text-slate-400">
                        Dossier nº {association.id}
                      </div>

                      {association.mail_asso ? (
                        <div
                          className="mt-1 max-w-52 truncate text-xs text-slate-500"
                          title={association.mail_asso}
                        >
                          {association.mail_asso}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </TableCell>

                <TableCell>
                  <div className="max-w-64">
                    <div className="inline-flex rounded-lg bg-violet-50 px-2 py-1 text-xs font-semibold text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
                      {association.produit_asso || "—"}
                    </div>

                    <div className="mt-2 flex flex-wrap gap-1">
                      {disciplines.length ? (
                        disciplines
                          .slice(0, 3)
                          .map((discipline) => (
                            <span
                              key={discipline}
                              className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                            >
                              {discipline}
                            </span>
                          ))
                      ) : (
                        <span className="text-xs italic text-slate-400">
                          Aucune discipline
                        </span>
                      )}

                      {disciplines.length > 3 ? (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          +{disciplines.length - 3}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </TableCell>

                <TableCell>
                  <InlineStatutSelect
                    value={
                      association.statut_asso || ""
                    }
                    updating={statusUpdating}
                    disabled={statusUpdating}
                    label={`Modifier le statut de ${association.nom_asso}`}
                    onChange={(newStatus) =>
                      onStatusChange?.(
                        association,
                        newStatus,
                      )
                    }
                  />
                </TableCell>

                <TableCell>
                  <FollowUpCell
                    association={association}
                  />
                </TableCell>

                <TableCell>
                  <div className="min-w-36">
                    <ContactList
                      prospects={
                        association.prospects || []
                      }
                    />
                  </div>
                </TableCell>

                <TableCell>
                  <AssociationActions
                    association={association}
                    deleting={
                      deletingId === association.id
                    }
                    onQuickView={onQuickView}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    className="justify-center"
                  />
                </TableCell>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default AssociationsTable;